import { Controller } from "@hotwired/stimulus"

// Gestes par lesquels le visiteur reprend la main sur le défilement, et lève donc le verrou posé par
// un clic dans le sommaire.
const RELEASE_EVENTS = ["wheel", "touchmove", "keydown"]

// Rangées visibles du bandeau avant qu'il ne se replie derrière sa pastille.
const MAX_ROWS = 2

// SOMMAIRE DE PAGE : il surligne l'entrée de la section en cours de lecture, replie ce qui déborde du
// bandeau, et adapte sa couleur au fond du bloc en regard.
//
// LA SECTION COURANTE EST CALCULÉE à chaque défilement — celle qui occupe la plus grande part de
// l'écran — et non déduite d'événements d'intersection : l'ordre n'y est pas garanti, la remontée
// demande de deviner la section précédente, et le dernier titre d'une page n'entre jamais dans la
// zone de détection.
//
// Le travail est fait dans un requestAnimationFrame : au plus une mesure par image, quel que soit le
// débit des événements de défilement.
export default class extends Controller {
  static targets = ["title", "link", "list", "item", "toggleItem", "toggle", "count"]

  // Libellés traduits côté serveur : le gabarit du compte, celui de son intitulé accessible, et
  // celui du repli. Le repli n'a pas de texte visible — c'est un chevron, posé dans la vue et montré
  // par le CSS.
  static values = {
    expandText: String,
    expandLabel: String,
    collapseLabel: String
  }

  // Opacité à partir de laquelle un fond décide de la couleur du sommaire. En dessous, il laisse voir
  // ce qui est derrière, et c'est ce qui est derrière qui compte : le voile d'une couverture, noir à
  // 30 %, ferait sinon passer le sommaire en blanc alors que la décision appartient à la section
  // qu'il recouvre.
  static COVERING_ALPHA = 0.9

  connect() {
    // Le repli des rangées ne dépend pas des titres de la page : il vaut même si aucune ancre n'a été
    // retrouvée, d'où sa mise en place avant la sortie plus bas.
    this.expanded = false
    this.fitRows = this.fitRows.bind(this)
    window.addEventListener("resize", this.fitRows, { passive: true })
    this.fitRows()

    // Hauteur du bandeau, publiée pour les ancres : elle CHANGE — une rangée, deux, ou tout déplié —
    // et aucune valeur en dur ne conviendrait. Sans elle, cliquer une entrée amène le titre visé
    // derrière le bandeau, et l'on atterrit au milieu du premier paragraphe de la section demandée.
    //
    // Un observateur plutôt qu'un appel dans fitRows : la hauteur bouge aussi quand les polices
    // arrivent ou quand le visiteur déplie, et fitRows sort par plusieurs chemins.
    this.resizeObserver = new ResizeObserver(() => this.#publishHeight())
    this.resizeObserver.observe(this.element)

    // Et une fois les polices arrivées. La première mesure a lieu avec la police de repli, plus
    // large : les entrées y réclament une rangée de plus, la pastille apparaît, puis la vraie police
    // les fait toutes tenir sans que rien ne le recalcule.
    document.fonts?.ready.then(() => this.fitRows())

    this.headings = this.linkTargets
      .map((link) => document.getElementById(link.dataset.outlineAnchor))
      .filter(Boolean)

    if (this.headings.length === 0) return

    this.onScroll = this.onScroll.bind(this)
    this.release = this.release.bind(this)
    window.addEventListener("scroll", this.onScroll, { passive: true })
    window.addEventListener("resize", this.onScroll, { passive: true })
    // Gestes de défilement du visiteur, par opposition au défilement que le sommaire déclenche
    // lui-même : c'est ce qui distingue « je reprends la lecture » de « la page va vers ma cible ».
    // L'événement `scroll` ne le dirait pas — il se produit dans les deux cas.
    RELEASE_EVENTS.forEach((name) => window.addEventListener(name, this.release, { passive: true }))
    this.update()
  }

  disconnect() {
    window.removeEventListener("scroll", this.onScroll)
    window.removeEventListener("resize", this.onScroll)
    window.removeEventListener("resize", this.fitRows)
    RELEASE_EVENTS.forEach((name) => window.removeEventListener(name, this.release))
    if (this.frame) cancelAnimationFrame(this.frame)
    this.resizeObserver?.disconnect()
    // Retirée et non laissée à sa dernière valeur : une page sans sommaire décalerait sinon ses
    // ancres de la hauteur d'un bandeau qui n'existe plus.
    document.documentElement.style.removeProperty("--site-outline-height")
  }

  // Ce que le bandeau recouvre, et que les ancres doivent dégager. Zéro pour la colonne latérale :
  // elle vit dans la marge et ne masque aucun titre.
  #publishHeight() {
    const height = this.#layout() === "banner" ? this.element.offsetHeight : 0
    document.documentElement.style.setProperty("--site-outline-height", `${height}px`)
  }

  // --- Deux rangées au plus, et une pastille pour le reste ------------------------------------
  //
  // Ce qui tient sur une rangée dépend de la largeur de l'écran ET de la longueur des intitulés :
  // aucune règle CSS ne sait le dire, seule une mesure le peut. On masque donc entrée par entrée
  // plutôt que de borner la hauteur de la liste — une hauteur bornée couperait aussi la pastille, et
  // le visiteur n'aurait plus rien pour ouvrir ce qu'on vient de lui cacher.
  fitRows() {
    if (!this.hasListTarget || this.itemTargets.length === 0) return

    // En colonne latérale, il n'y a ni rangée ni pastille : on rend leur visibilité aux entrées que
    // le bandeau avait masquées, sans quoi un écran élargi garderait des sections disparues.
    if (this.#layout() !== "banner") return this.#showEveryItem()

    // Déplié par le visiteur : sa demande tient jusqu'à ce qu'il replie, redimensionnement compris.
    // On rétablit l'état déplié EN ENTIER, pastille comprise — un aller-retour par la colonne l'a
    // masquée, et sans cela le sommaire resterait ouvert sans plus rien pour le refermer.
    if (this.expanded) return this.#showExpanded()

    this.#showEveryItem()
    if (this.#rowCount() <= MAX_ROWS) return

    // Il déborde : la pastille entre en scène, et on masque les entrées EN PARTANT DE LA FIN jusqu'à
    // ce que tout tienne — celles qui restent ET la pastille, qui occupe elle-même une place.
    //
    // On masque AVANT d'écrire le compte : l'écrire d'abord le poserait à zéro, ce que la pastille
    // lit comme « la liste est ouverte » et qui lui fait montrer le chevron du repli.
    this.toggleItemTarget.hidden = false
    let visibleCount = this.itemTargets.length

    while (visibleCount > 0) {
      visibleCount -= 1
      this.itemTargets[visibleCount].hidden = true
      this.#writeToggle(this.itemTargets.length - visibleCount)
      if (this.#rowCount() <= MAX_ROWS) break
    }
  }

  toggle() {
    this.expanded = !this.expanded
    if (this.expanded) return this.#showExpanded()

    this.fitRows()
  }

  // Tout est visible, et la pastille reste là pour refermer.
  #showExpanded() {
    this.#showEveryItem()
    this.toggleItemTarget.hidden = false
    this.#writeToggle(0)
  }

  #showEveryItem() {
    this.itemTargets.forEach((item) => { item.hidden = false })
    this.toggleItemTarget.hidden = true
  }

  // Le compte affiché sur la pastille et son intitulé accessible. `hiddenCount` à zéro : la liste est
  // ouverte, la pastille sert à la refermer et c'est le chevron qui s'affiche — `aria-expanded`
  // suffit à le décider, la bascule des deux faces étant écrite en CSS.
  #writeToggle(hiddenCount) {
    const open = hiddenCount === 0
    this.countTarget.textContent = open ? "" : this.expandTextValue.replace("%{count}", hiddenCount)
    this.toggleTarget.setAttribute(
      "aria-label",
      open ? this.collapseLabelValue : this.expandLabelValue.replace("%{count}", hiddenCount)
    )
    this.toggleTarget.setAttribute("aria-expanded", String(open))
  }

  // Le nombre de rangées occupées, compté sur les ORDONNÉES distinctes de ce qui est affiché — la
  // pastille comprise, qui prend sa place dans la rangée.
  //
  // Compté et non déduit d'une hauteur : « deux fois la hauteur d'une entrée, plus l'espacement » a
  // l'air exact et ne l'est pas. Les rangées n'ont pas toutes la même hauteur, et l'arrondi d'un
  // demi-pixel suffit à faire croire au débordement.
  #rowCount() {
    const tops = new Set()
    const add = (element) => {
      if (!element.hidden) tops.add(Math.round(element.getBoundingClientRect().top))
    }

    this.itemTargets.forEach(add)
    add(this.toggleItemTarget)
    return tops.size
  }

  // La mise en page en cours, telle que la FEUILLE DE STYLE la déclare : le seuil de bascule vers la
  // colonne y est écrit une seule fois, et le redéclarer ici finirait par diverger de lui.
  #layout() {
    return getComputedStyle(this.element).getPropertyValue("--outline-layout").trim()
  }

  release() {
    this.lockedAnchor = null
  }

  onScroll() {
    if (this.frame) return

    this.frame = requestAnimationFrame(() => {
      this.frame = null
      this.update()
    })
  }

  // ENTRÉE COURANTE = LA SECTION QUI OCCUPE LA PLUS GRANDE PART DE L'ÉCRAN.
  //
  // C'est une mesure de surface, pas un seuil. Un seuil — « le dernier titre passé au-dessus de telle
  // ligne » — se trompe forcément dans un sens ou dans l'autre selon la longueur des sections : il
  // bascule trop tôt quand un titre apparaît alors qu'on lit encore la section précédente, trop tard
  // quand un grand titre occupe déjà tout l'écran.
  //
  // La surface visible règle aussi le bas de page sans cas particulier : la dernière section y est
  // seule à l'écran, donc elle gagne.
  update() {
    // Après un clic dans le sommaire, l'entrée demandée reste marquée jusqu'à ce que le visiteur
    // reprenne la main. Sans ce verrou, cliquer sur une section très courte marquerait aussitôt la
    // suivante — celle qui remplit l'écran — alors qu'on vient justement de demander la première.
    if (this.lockedAnchor) {
      this.adaptToBackdrop()
      return
    }

    const top = this.#maskedHeight()
    const bottom = window.innerHeight
    let current = null
    let largest = 0

    this.headings.forEach((heading, index) => {
      const start = heading.getBoundingClientRect().top
      const next = this.headings[index + 1]
      // La section court de son titre au titre suivant ; la dernière court jusqu'au bas du document.
      const end = next ? next.getBoundingClientRect().top : document.documentElement.scrollHeight
      const visible = Math.min(end, bottom) - Math.max(start, top)

      if (visible > largest) {
        largest = visible
        current = heading
      }
    })

    // Ce qui précède la première section — une couverture, un chapô — n'appartient à aucune d'elles.
    // Si cette zone occupe plus d'écran que n'importe quelle section, rien n'est surligné : on n'y
    // est pas.
    const beforeFirst = Math.min(this.headings[0].getBoundingClientRect().top, bottom) - top
    if (beforeFirst > largest) current = null

    this.highlight(current)
    this.adaptToBackdrop()
  }

  // Ce que le sommaire masque en haut de fenêtre, et qui ne compte donc pas comme lu.
  //
  // ÉCART AVEC LE PROJET DE RÉFÉRENCE : son en-tête est fixe et publie sa hauteur, qu'il faut ajouter
  // ici. Le nôtre défile avec la page — seul le bandeau collant masque quelque chose, et la colonne
  // latérale ne masque rien.
  #maskedHeight() {
    return this.#layout() === "banner" ? this.element.offsetHeight : 0
  }

  highlight(current) {
    this.linkTargets.forEach((link) => {
      const active = link.dataset.outlineAnchor === current?.id
      link.toggleAttribute("data-active", active)
      if (active) link.setAttribute("aria-current", "true")
      else link.removeAttribute("aria-current")
    })
  }

  // LE SOMMAIRE N'A PAS DE FOND EN COLONNE : ses traits et ses libellés s'inversent au-dessus d'une
  // bande sombre.
  //
  // LIGNE PAR LIGNE, et non pour la colonne entière. Une seule mesure, prise en son milieu, déciderait
  // pour toutes les entrées : dès que la colonne chevauche la frontière de deux sections de teintes
  // opposées — le cas ordinaire, la colonne étant haute et les bandes pleine largeur — la moitié des
  // entrées hériterait de la couleur de l'autre moitié, et disparaîtrait dans son fond.
  //
  // On mesure ce qui se trouve DERRIÈRE chaque ligne, à sa hauteur réelle, et non le fond du bloc dont
  // le titre est actif : la colonne est centrée verticalement, alors que le titre courant est plus
  // haut.
  adaptToBackdrop() {
    // Le bandeau a un fond opaque : rien n'y transparaît, et aucune règle n'y répond à l'attribut.
    if (this.#layout() === "banner") return

    // Un seul x pour toutes les lignes, pris juste à droite de la colonne : les fonds sont des bandes
    // horizontales pleine largeur, la hauteur seule décide donc de la teinte. À droite plutôt qu'au
    // milieu d'une entrée, pour ne pas avoir à suivre une largeur qui change au survol.
    const x = Math.round(this.element.getBoundingClientRect().right + 4)

    this.#tintable().forEach((element) => this.#tintAgainstBackdrop(element, x))
  }

  // Ce qui porte une couleur propre : le titre et les entrées. La pastille n'en est pas — la colonne
  // la masque, elle n'appartient qu'au bandeau.
  #tintable() {
    return this.hasTitleTarget ? [this.titleTarget, ...this.linkTargets] : this.linkTargets
  }

  #tintAgainstBackdrop(element, x) {
    const rect = element.getBoundingClientRect()
    // Sans hauteur, l'élément est masqué : le mesurer viserait le haut de la fenêtre et lui donnerait
    // la couleur d'un fond qui n'est pas le sien.
    if (rect.height === 0) return

    // On mesure les DEUX BORDS, et non le seul milieu : une ligne peut à son tour enjamber la
    // frontière de deux fonds contraires, et aucune couleur unique ne la rend alors lisible de bout
    // en bout. Deux pixels vers l'intérieur, pour mesurer la ligne et non ce qui la borde.
    const inset = 2
    const atTop = this.#isDarkBackdrop(x, Math.round(rect.top + inset))
    const atBottom = this.#isDarkBackdrop(x, Math.round(rect.bottom - inset))
    const straddling = atTop !== atBottom

    // Le milieu départage la ligne à cheval — sa couleur est celle du fond qui l'occupe le plus. Il
    // n'est mesuré que dans ce cas : bords concordants, il ne peut que leur ressembler, et cette
    // mesure a lieu pour chaque ligne à chaque image.
    const onDark = straddling ? this.#isDarkBackdrop(x, Math.round(rect.top + rect.height / 2)) : atTop

    element.toggleAttribute("data-on-dark", onDark)
    element.toggleAttribute("data-straddling", straddling)
  }

  // Le fond sous ce point est-il sombre ? Aucun fond couvrant, c'est le blanc de la page : clair.
  #isDarkBackdrop(x, y) {
    const behind = this.#backdropAt(x, y)
    return behind ? this.#isDark(behind) : false
  }

  // Premier fond COUVRANT sous ce point, ou null. La pile est parcourue du dessus vers la racine et
  // l'on s'arrête au premier fond qui décide.
  #backdropAt(x, y) {
    for (const candidate of document.elementsFromPoint(x, y)) {
      if (this.element.contains(candidate)) continue

      const color = this.#backgroundOf(candidate)
      if (color.alpha >= this.constructor.COVERING_ALPHA) return color
    }
    return null
  }

  // Composantes d'un fond, quel que soit le FORMAT dans lequel le navigateur l'exprime.
  //
  // Lire les trois premiers nombres de la chaîne ne vaudrait que pour `rgb()`. Or le navigateur
  // renvoie aussi `oklab()` et `color(srgb …)`, que Tailwind v4 produit : « oklab(0.97 0 0) », un gris
  // très clair, y deviendrait rouge 0, vert 97, bleu 0, donc SOMBRE — et le sommaire s'écrirait en
  // blanc sur un fond clair.
  //
  // Le canevas fait la conversion que le navigateur sait déjà faire, et qu'il serait vain de réécrire
  // format par format : on peint la couleur sur un pixel, et on le relit. L'entrée vient toujours de
  // `getComputedStyle`, donc d'une valeur que le navigateur a lui-même calculée : elle est valide.
  #backgroundOf(element) {
    const context = this.#pixel()
    context.clearRect(0, 0, 1, 1)
    context.fillStyle = getComputedStyle(element).backgroundColor
    context.fillRect(0, 0, 1, 1)

    const [red, green, blue, alpha] = context.getImageData(0, 0, 1, 1).data
    return { red, green, blue, alpha: alpha / 255 }
  }

  // Un pixel hors écran, créé une fois : cette mesure a lieu à chaque défilement.
  #pixel() {
    if (!this.paintContext) {
      const canvas = document.createElement("canvas")
      canvas.width = 1
      canvas.height = 1
      this.paintContext = canvas.getContext("2d", { willReadFrequently: true })
    }
    return this.paintContext
  }

  // Luminance perçue (recommandation WCAG) plutôt qu'une liste de couleurs connues : le fond peut
  // venir d'une image de couverture, d'une classe utilitaire ou d'un réglage de bloc.
  #isDark({ red, green, blue }) {
    return (0.299 * red + 0.587 * green + 0.114 * blue) / 255 < 0.5
  }

  // Turbo Drive intercepte le clic sur un lien d'ancre : l'adresse change, mais la page ne défile
  // pas. On fait donc le déplacement nous-mêmes. `scrollIntoView` respecte le `scroll-margin-top`
  // posé en CSS, donc la section arrive sous le bandeau et non derrière lui.
  scrollTo(event) {
    const anchor = event.currentTarget.dataset.outlineAnchor
    const heading = document.getElementById(anchor)
    if (!heading) return

    event.preventDefault()
    // L'entrée demandée est marquée tout de suite et le reste jusqu'au prochain geste du visiteur :
    // ce qu'il a demandé prime sur ce que la mesure de surface dirait pendant et après le trajet.
    this.lockedAnchor = anchor
    this.highlight(heading)

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    heading.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" })
    // L'adresse reste partageable, sans ajouter une entrée d'historique par clic.
    history.replaceState(history.state, "", `#${anchor}`)
  }
}
